<script>
    import { onMount } from "svelte";

    import { user } from "../../stores";

    import {
        PageHead,
        PageLoading,
        RecordPaymentModal,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        getValidBillingData,
        goBack,
        goToDownloadPage,
        isObjectEmpty,
        showError,
        showException,
    } from "../../Utils/utilFunctions.svelte";

    import {
        getInsuranceFundById,
        getTrafficAccidentAuthorityById,
        getWorkerCompensationById,
    } from "../../Utils/utilCache.svelte";

    import ViewInvoicesHeadDetails from "./components/ViewInvoicesHeadDetails.svelte";
    import ViewInvoicesPatientDetails from "./components/ViewInvoicesPatientDetails.svelte";
    import ViewInvoicesProcedureDetails from "./components/ViewInvoicesProcedureDetails.svelte";
    import ViewInvoicesInvoiceDetails from "./components/ViewInvoicesInvoiceDetails.svelte";
    import ViewInvoicesBillingDetails from "./components/ViewInvoicesBillingDetails.svelte";
    import ViewInvoicesPaymentTable from "./components/ViewInvoicesPaymentTable.svelte";

    import { headerRightBtnCls } from "../../Utils/utilConstants.svelte";

    export let params = {};

    let isLoading = false,
        isDownloadingPDF = false,
        isCancelInvoiceModelOpen = false,
        isRecordPaymentModalOpen = false,
        isCancellingInvoice = false,
        invoiceDetails = {},
        backupInvoiceDetails = {},
        procedureDetails = {},
        doctorDetails = {},
        patientDetails = {},
        patientEncounterDetails = {},
        paymentDetails = {};

    let isEditEnabled = false,
        isUpdatingInvoice = false;

    let isInvoiceEnableUncancel = false;

    let invoicePayments = [];

    $: if (
        params.id &&
        procedureDetails.patientEncounterId &&
        doctorDetails.id &&
        patientDetails.id &&
        patientEncounterDetails.id
    )
        isLoading = false;

    $: isUserAdmin = $user.role == 100;

    $: isUserDoctor = $user.role == 2;

    $: isUserEnableSeePaymentReceive = $user.role >= 20;

    $: isUserEnableCancelInvoice =
        isUserAdmin || $user.role == 20 || isUserDoctor;

    // Only allow invoice deletion if it has no payments or if all payments are either Cancelled (9) or Refunded (4, 5)
    $: isInvoiceDeletable =
        invoiceDetails.status != 9 &&
        (!invoiceDetails.payments?.length ||
            invoiceDetails.payments.every((payment) =>
                [9].includes(payment.status),
            ));

    $: isDeleteInvoiceBtnDisabled = isCancellingInvoice;

    onMount(() => {
        if (params.id) getInvoiceDetails(params.id);
    });

    const getInvoiceDetails = (id) => {
        isLoading = true;

        apiMethods.getMethod.getInvoiceById(id).then((res) => {
            if (res.id) {
                invoiceDetails = { ...res };

                _updateBillingItemsData();

                _updatePaymentDetails();

                loadPatientEncounterDetails(res.patientEncounterId);

                backupInvoiceDetails = { ...invoiceDetails };

                invoicePayments = invoiceDetails.payments;
            }
        });
    };

    const reload = () => {
        if (params.id) getInvoiceDetails(params.id);
    };

    const reloadPayments = async () => {
        await apiMethods.getMethod.getInvoiceById(params.id).then((res) => {
            invoicePayments = [];
            invoiceDetails = { ...res };
            _updatePaymentDetails();
            backupInvoiceDetails = { ...invoiceDetails };
        });
        invoicePayments = invoiceDetails.payments;
    };

    const loadPatientEncounterDetails = (id) => {
        apiMethods.getMethod
            .getPatientEncounterById(id, true)
            .then(async (res) => {
                if (res.id) {
                    patientEncounterDetails = { ...res };

                    await _updatePatientDetails();

                    _updateProcedureDetails();

                    _updateDoctorDetails();

                    isInvoiceEnableUncancel = _checkInvoiceEnableUncancel();
                } else
                    showError(
                        "Unable to load patient encounter details",
                        goBack,
                    );
            });
    };

    const getFile = () => {
        goToDownloadPage(1, invoiceDetails.id);
    };

    const cancelInvoice = async () => {
        isCancellingInvoice = true;

        if (isInvoiceDeletable)
            await apiMethods.putMethod
                .updateInvoiceStatus(invoiceDetails.id, 9)
                .then((res) => {
                    if (res.error) showError(res.error);
                    else {
                        invoiceDetails = { ...res };
                        backupInvoiceDetails = { ...res };
                    }
                })
                .catch((err) => showException(err));
        else if (isInvoiceEnableUncancel)
            await apiMethods.putMethod
                .updateInvoiceStatus(invoiceDetails.id, 1)
                .then((res) => {
                    if (res.error) showError(res.error);
                    else {
                        invoiceDetails = { ...res };
                        backupInvoiceDetails = { ...res };
                    }
                })
                .catch((err) => showException(err));

        isInvoiceEnableUncancel = _checkInvoiceEnableUncancel();

        isCancellingInvoice = false;

        closeCancelInvoiceModel();
    };

    const openCancelInvoiceModel = () => {
        isCancelInvoiceModelOpen = true;
    };

    const closeCancelInvoiceModel = () => {
        isCancelInvoiceModelOpen = false;
    };

    const openRecordPaymentModal = (paymentData = {}) => {
        isRecordPaymentModalOpen = true;

        if (!isObjectEmpty(paymentData))
            paymentDetails = {
                ...paymentData,
                isEditPayment: true,
                billingDetails: backupInvoiceDetails.billingDetails,
            };
    };

    const closeRecordPaymentModal = (needReload, data) => {
        isRecordPaymentModalOpen = false;

        paymentDetails = { ...data };
    };

    const saveInvoice = async () => {
        isUpdatingInvoice = true;

        await apiMethods.putMethod
            .updateInvoice(invoiceDetails)
            .then((res) => {
                if (res.id) reload();
                else {
                    showError(res.error);
                    cancelEditInvoice;
                }
            })
            .catch((err) => {
                showException(err);
                cancelEditInvoice();
            });

        isUpdatingInvoice = false;
        isEditEnabled = false;
    };

    const cancelEditInvoice = () => {
        isEditEnabled = false;
        invoiceDetails = { ...backupInvoiceDetails };
    };

    const _updateBillingItemsData = () => {
        if (invoiceDetails.billingItems.length) {
            const lastItem = {
                isLast: true,
                totalAmount: invoiceDetails.billingItems.reduce(
                    (sum, { totalAmount }) => sum + totalAmount,
                    0,
                ),
            };

            invoiceDetails.billingItems.push(lastItem);
        }
    };

    const _updatePaymentDetails = () => {
        paymentDetails.relId = invoiceDetails.id;
        paymentDetails.doctorId = invoiceDetails.doctorId;
        paymentDetails.patientId = invoiceDetails.patientId;
        paymentDetails.patientEncounterId = invoiceDetails.patientEncounterId;
        paymentDetails.dueDate = invoiceDetails.dueDate;
        paymentDetails.billingDetails = invoiceDetails.billingDetails;
    };

    const _updateProcedureDetails = () => {
        const {
            procedureName,
            scheduledDate,
            dischargeDate,
            lengthInHours,
            createdDate,
            updatedDate,
            neuraxialType,
            typeOfBill,
        } = patientEncounterDetails;

        procedureDetails = {
            procedureName,
            scheduledDate,
            dischargeDate,
            lengthInHours,
            createdDate,
            updatedDate,
            neuraxialType,
            typeOfBill,
            patientEncounterId: patientEncounterDetails.id,
        };

        if (patientEncounterDetails.hospital?.id) {
            procedureDetails = {
                ...procedureDetails,
                hospital: { ...patientEncounterDetails.hospital },
            };
        } else procedureDetails.hospital = {};

        if (patientEncounterDetails.surgeon?.id) {
            procedureDetails = {
                ...procedureDetails,
                surgeon: { ...patientEncounterDetails.surgeon },
            };
        } else procedureDetails.surgeon = {};
    };

    const _updatePatientDetails = async () => {
        const {
            opvStatusMedicare,
            opvStatusHealthFund,
            opvManuallyApproved,
            opvStatusVeteran,
        } = patientEncounterDetails;

        patientDetails = {
            ...patientEncounterDetails.patient,
            opvStatusMedicare,
            opvStatusHealthFund,
            opvManuallyApproved,
            opvStatusVeteran,
            patientEncounterId: patientEncounterDetails.id,
        };

        if (patientEncounterDetails.billingData.length) {
            const lastBillingData = getValidBillingData(
                patientEncounterDetails.billingData,
            );

            const {
                patientInsurance,
                patientInsuranceType,
                patientInsuranceId,
            } = lastBillingData;

            patientDetails.patientInsurance = patientInsurance;

            if (patientInsuranceId) {
                let res =
                    patientInsuranceType == 0
                        ? await getInsuranceFundById(patientInsuranceId)
                        : patientInsuranceType == 16
                          ? await getWorkerCompensationById(patientInsuranceId)
                          : patientInsuranceType == 17
                            ? await getTrafficAccidentAuthorityById(
                                  patientInsuranceId,
                              )
                            : {};

                patientDetails.insuranceName = res?.name ?? "";
            } else patientDetails.insuranceName = "";
        } else
            patientDetails = {
                ...patientDetails,
                insuranceName: "",
                patientInsurance: "",
            };
    };

    const _updateDoctorDetails = () => {
        const {
            doctor: {
                firstName,
                lastName,
                fullName,
                title,
                phone,
                id,
                profile,
            },
            doctorProvider: {
                number,
                location,
                locationAddress,
                locationAddress2,
                city,
                state,
                postCode,
                isNoGapBupa,
                isNoGapHCF,
                isNoGapNib,
                isPreferredDefence,
            },
        } = patientEncounterDetails;

        doctorDetails = {
            firstName,
            lastName,
            fullName,
            title,
            phone,
            id,
            number,
            location,
            locationAddress,
            locationAddress2,
            city,
            state,
            postCode,
            isNoGapBupa,
            isNoGapHCF,
            isNoGapNib,
            isPreferredDefence,
            profile,
        };
    };

    const _checkInvoiceEnableUncancel = () => {
        if (invoiceDetails.status != 9) return false;

        const { invoices } = patientEncounterDetails;

        if (invoices.length == 1) return true;

        return !invoices
            .filter((invoice) => invoice.id != invoiceDetails.id)
            .some((invoice) => invoice.status != 9);
    };
</script>

{#if isLoading}
    <PageLoading />
{:else}
    <PageHead title="View invoice details">
        {#if isInvoiceDeletable && isUserEnableCancelInvoice}
            <button
                class="btn-warning {headerRightBtnCls}"
                on:click={openCancelInvoiceModel}
            >
                <i class="fa fa-times"></i>
                Cancel Invoice
            </button>
        {:else if isInvoiceEnableUncancel && isUserEnableCancelInvoice}
            <button
                class="btn-warning {headerRightBtnCls}"
                on:click={openCancelInvoiceModel}
            >
                <i class="fa-solid fa-rotate-left"></i>
                Uncancel Invoice
            </button>
        {/if}

        {#if isUserAdmin && backupInvoiceDetails.status !== Number(9)}
            <button
                class={headerRightBtnCls}
                on:click={() => openRecordPaymentModal()}
            >
                <i class="fa fa-credit-card"></i>
                Record Payment
            </button>
        {/if}

        <button
            class={`${headerRightBtnCls} ${isDownloadingPDF ? "loading" : ""}`}
            on:click={getFile}
            disabled={isDownloadingPDF}
        >
            <i class="fas fa-download" />
            {isDownloadingPDF ? "Downloading PDF" : "Download invoice PDF"}
        </button>
    </PageHead>

    <div class="flex-1 relative mt-4">
        <div class="flex flex-col overflow-y-auto absolute inset-0 gap-4 px-2">
            <ViewInvoicesHeadDetails invoiceDetails={backupInvoiceDetails} />

            <ViewInvoicesPatientDetails
                {patientDetails}
                {patientEncounterDetails}
                loadData={reload}
            />

            <ViewInvoicesProcedureDetails {procedureDetails} {doctorDetails} />

            <ViewInvoicesInvoiceDetails
                {patientDetails}
                bind:invoiceDetails
                bind:isEditEnabled
                bind:isUpdatingInvoice
                isEnableEdit
                on:saveInvoice={saveInvoice}
                on:cancelEditInvoice={cancelEditInvoice}
            />

            <ViewInvoicesBillingDetails
                billingItems={backupInvoiceDetails.billingItems}
            />

            <ViewInvoicesPaymentTable
                {invoicePayments}
                {isUserDoctor}
                {patientDetails}
                {isUserEnableSeePaymentReceive}
                reload={reloadPayments}
                {openRecordPaymentModal}
            />
        </div>
    </div>
{/if}

<div
    class={isCancelInvoiceModelOpen
        ? "modal modal-open sm:modal-middle"
        : "modal"}
>
    <div class="modal-box">
        <div class="p-4">
            <p class="leading-6 text-error">
                {isInvoiceDeletable ? "Cancel" : "Uncancel"} this invoice?
            </p>
        </div>
        <div class="modal-action flex items-center justify-between flex-1">
            <button
                class="btn btn-secondary text-white btn-sm border-none w-48"
                on:click={closeCancelInvoiceModel}>Cancel</button
            >
            <button
                class={`btn btn-error text-white btn-sm border-none w-48 ${
                    isDeleteInvoiceBtnDisabled ? "loading" : ""
                }`}
                on:click={cancelInvoice}
                disabled={isDeleteInvoiceBtnDisabled}
            >
                {isDeleteInvoiceBtnDisabled ? "Processing" : "Confirm"}
            </button>
        </div>
    </div>
</div>

{#if isRecordPaymentModalOpen}
    <RecordPaymentModal
        {isRecordPaymentModalOpen}
        {paymentDetails}
        route="invoice"
        {closeRecordPaymentModal}
        on:paymentRecorded={reloadPayments}
    />
{/if}
